import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    ProtoApiService,
    ProtoRequest,
    ProtoResponse
} from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { filter, startWith } from 'rxjs/operators';
import { Date, Error, Money } from './models';

@Injectable({ providedIn: 'root' })
export class CreateFundingBatchDetailService extends ProtoApiService<
    CreateFundingBatchDetailRequest,
    CreateFundingBatchDetailResponse
> {
    public requestType = CreateFundingBatchDetailRequest;
    public responseType = CreateFundingBatchDetailResponse;
    public endpointName = 'CreateFundingBatchDetail';
    public serviceName = 'financing';
    public constructor(
        _http: HttpClient,
        @Inject(SETTINGS) _settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(_http, _settings);

        // TODO: pay-later is a buyer-level feature flag, which we don't currently support on the front end.
        // Support will involve adding an API call to get the associated buyer.
        _featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(
                startWith(_featureFlagService.isOnSync(FeatureFlags.PayLater)),
                filter((isPayLaterOn) => isPayLaterOn)
            )
            .subscribe(() => {
                this.endpointName = 'v1/fundingRequest';
                this.serviceName = 'fundingApi';
                this.usePascalForUrl = false;
            });
    }
    public requestMapper(
        req: CreateFundingBatchDetailRequest
    ): GetFundingRequest | CreateFundingBatchDetailRequest {
        if (this._featureFlagService.isOnSync(FeatureFlags.PayLater)) {
            return new GetFundingRequest({
                bankAccountKey: req.refBankAccountKey,
                requestDate:
                    req.requestDate.year + '-' + req.requestDate.month + '-' + req.requestDate.day,
                aprovals: req.refScfApprovalKeys.map((key) => +key)
            });
        } else {
            return req;
        }
    }

    public responseMapper(
        res: GetFundingResponse
    ): CreateFundingBatchDetailResponse | GetFundingResponse {
        if (this._featureFlagService.isOnSync(FeatureFlags.PayLater)) {
            return new CreateFundingBatchDetailResponse({
                ok: res.ok,
                availableAmountTotal: res.availableAmountTotal,
                error: res.error
            });
        } else {
            return res;
        }
    }
}
export class GetFundingRequest {
    public bankAccountKey: string;
    public requestDate: string;
    public aprovals: number[];

    constructor(params?: Partial<GetFundingRequest>) {
        this.bankAccountKey = params?.bankAccountKey || null;
        this.requestDate = params?.requestDate || null;
        this.aprovals = params?.aprovals || null;
    }
}

export class GetFundingResponse {
    public error: Error;
    public availableAmountTotal: Money;
    public ok: boolean = null;

    constructor(obj?: Partial<GetFundingResponse>) {
        this.ok = true;
        this.error = obj?.error || null;
        this.availableAmountTotal = obj?.availableAmountTotal || null;
    }
}

export class CreateFundingBatchDetailRequest extends ProtoRequest {
    public refSellerKey: string;
    public refBankAccountKey: string;
    public refScfInvoiceKeys: string[];
    public refScfApprovalKeys: string[];
    public requestDate: Date;
    constructor(obj?: {
        refSellerKey?: string;
        refBankAccountKey?: string;
        refScfInvoiceKeys?: string[];
        refScfApprovalKeys?: string[];
        requestDate?: Date;
    }) {
        super();
        if (obj != null) {
            this.refSellerKey =
                typeof obj.refSellerKey === 'undefined' ? this.refSellerKey : obj.refSellerKey;
            this.refBankAccountKey =
                typeof obj.refBankAccountKey === 'undefined'
                    ? this.refBankAccountKey
                    : obj.refBankAccountKey;
            this.refScfInvoiceKeys =
                typeof obj.refScfInvoiceKeys === 'undefined'
                    ? this.refScfInvoiceKeys
                    : obj.refScfInvoiceKeys;
            this.refScfApprovalKeys =
                typeof obj.refScfApprovalKeys === 'undefined'
                    ? this.refScfApprovalKeys
                    : obj.refScfApprovalKeys;
            this.requestDate =
                typeof obj.requestDate === 'undefined' ? this.requestDate : obj.requestDate;
        }
    }
}

export class CreateFundingBatchDetailResponse extends ProtoResponse {
    public ok = false;
    public availableAmountTotal = new Money();
    public error: Error = null;
    constructor(obj?: { ok?: boolean; availableAmountTotal?: Money; error?: Error }) {
        super();
        if (obj != null) {
            const oneofSets = [['availableAmountTotal', 'error']];
            oneofSets.forEach((oneof) => {
                const keysAssigned = oneof.filter((key) => typeof obj[key] !== 'undefined');
                const shouldThrow = keysAssigned.length > 1;
                if (shouldThrow) {
                    throw new (window as any).Error(
                        'Attempted to assign more than one property from a "oneof" definition.\n' +
                            'May assign one of ' +
                            oneof.join(' or ') +
                            '.\n' +
                            'Attempted to assign ' +
                            keysAssigned.join(' and ') +
                            '.'
                    );
                }
            });

            this.ok = typeof obj.ok === 'undefined' ? this.ok : obj.ok;
            this.availableAmountTotal =
                typeof obj.availableAmountTotal === 'undefined'
                    ? this.availableAmountTotal
                    : obj.availableAmountTotal;
            this.error = typeof obj.error === 'undefined' ? this.error : obj.error;
        }
    }
}
